// @oasis-permission-inherits: /organizations (orgs:view)
import { Icon } from "@mirage-web/icons";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { useAuthStore } from "~/modules/auth/auth-store";
import { CreditDebitDialog } from "~/modules/credits/components/credit-debit-dialog";
import { CreditExpireDialog } from "~/modules/credits/components/credit-expire-dialog";
import { CreditGrantDialog } from "~/modules/credits/components/credit-grant-dialog";
import { OrgInfo } from "~/modules/lookup/components/org-info";
import { useAutoSaveSearch } from "~/modules/lookup/hooks/use-saved-searches";
import { lookupOrgBillingQuery, lookupOrgByIdQuery } from "~/modules/lookup/queries";
import { ConfigureSeatsCreditsDialog } from "~/modules/org/components/configure-seats-credits-dialog";
import { ConvertOrgToEnterpriseDialog } from "~/modules/org/components/convert-org-to-enterprise-dialog";
import { InviteMembersDialog } from "~/modules/org/components/invite-members-dialog";

const querySchema = z.object({
  sheet: z.enum(["grant", "debit", "config", "convert", "invite", "expire"]).optional(),
});

export const Route = createFileRoute("/_dash/organizations/view/$orgId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.orgId) {
      throw new Error("Organization ID is required");
    }

    queryClient.prefetchQuery(
      lookupOrgByIdQuery.queryOptions({ pathParams: { orgId: params.orgId } })
    );
  },
  pendingComponent: PendingComponent,
  validateSearch: querySchema,
});

function PendingComponent() {
  return (
    <div className="space-y-6">
      <div>
        <Link to="/organizations" className="text-sm text-muted-foreground hover:underline">
          ← Back to Organizations
        </Link>
      </div>

      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <Skeleton className="h-6 w-48" />
            <div className="flex items-center gap-2 mt-2">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-16" />
            </div>
            <div className="mt-2">
              <Skeleton className="h-6 w-48" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <Skeleton className="h-5 w-36" />
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Skeleton className="h-5 w-24" />
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-6" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-8" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-6 w-16" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>

          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between rounded-md border p-3">
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { orgId } = Route.useParams();
  const { sheet } = Route.useSearch();
  const [user] = useAuthStore((s) => [s.user]);
  const hasGrantCreditsPermission = user?.permissions.includes("credits:grant") ?? false;

  // Deliberately not prefetched in the loader: billing hits RevenueCat/Stripe
  // live, so it streams in after the org itself has rendered.
  const billingQuery = lookupOrgBillingQuery.useQuery({ pathParams: { orgId } });
  const { data } = lookupOrgByIdQuery.useSuspenseQuery({
    pathParams: { orgId },
  });

  const searchData = useMemo(() => {
    return data?.org?.orgDetails ? { name: data.org.orgDetails.name } : undefined;
  }, [data?.org?.orgDetails]);

  useAutoSaveSearch(orgId, searchData, "org");

  if (!data?.org) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/organizations" className="text-sm text-muted-foreground hover:underline">
            ← Back to Organizations
          </Link>
        </div>
        <div className="rounded-md border bg-muted/20 p-4">
          <h3 className="font-semibold">No Organization Found</h3>
          <p className="text-sm text-muted-foreground">No organization found with ID: {orgId}</p>
        </div>
      </div>
    );
  }

  const org = data.org;

  return (
    <div className="space-y-6">
      {sheet === "expire" && (
        <CreditExpireDialog
          orgId={orgId}
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
        />
      )}
      {sheet === "grant" && (
        <CreditGrantDialog
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
          preset={{ type: "org", id: org.orgDetails.id }}
        />
      )}
      {sheet === "debit" && (
        <CreditDebitDialog
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
          preset={{ type: "org", id: org.orgDetails.id }}
        />
      )}
      {sheet === "config" && (
        <ConfigureSeatsCreditsDialog
          orgId={orgId}
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
        />
      )}
      {sheet === "invite" && (
        <InviteMembersDialog
          orgId={orgId}
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
        />
      )}
      {sheet === "convert" && (
        <ConvertOrgToEnterpriseDialog
          orgId={orgId}
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
        />
      )}

      <div className="flex">
        <div className="flex flex-1">
          <Link to="/organizations" className="text-sm text-muted-foreground hover:underline">
            ← Back to Organizations
          </Link>
        </div>

        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Icon name="options" className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link
                to="/videos"
                search={{ type: "org", id: orgId }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem>
                  <Icon name="teams" className="h-4 w-4 mr-2" />
                  View Mirage videos
                </DropdownMenuItem>
              </Link>
              <Link
                to="/ai-edits/all"
                search={{ surface: "website", orgIds: orgId }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem>
                  <Icon name="edit-bare" className="h-4 w-4 mr-2" />
                  View AI edits
                </DropdownMenuItem>
              </Link>
              <Link
                to="/charges/organizations/$orgId"
                params={{ orgId }}
                className="text-sm text-primary hover:underline"
                disabled={!hasGrantCreditsPermission}
              >
                <DropdownMenuItem disabled={!hasGrantCreditsPermission}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Charge History
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "grant" }}
                className="text-sm text-primary hover:underline"
                disabled={!hasGrantCreditsPermission}
              >
                <DropdownMenuItem disabled={!hasGrantCreditsPermission}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Grant credits
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "debit" }}
                className="text-sm text-primary hover:underline"
                disabled={!hasGrantCreditsPermission}
              >
                <DropdownMenuItem disabled={!hasGrantCreditsPermission}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Debit credits
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "expire" }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Expire credits for debug
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "convert" }}
                className="text-sm text-primary hover:underline"
                disabled={org.orgDetails.skuba?.plan === "enterprise"}
              >
                <DropdownMenuItem disabled={org.orgDetails.skuba?.plan === "enterprise"}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Convert this Organization to Enterprise plan (for non-enterprise orgs)
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "config" }}
                className="text-sm text-primary hover:underline"
                disabled={org.orgDetails.skuba?.plan !== "enterprise"}
              >
                <DropdownMenuItem disabled={org.orgDetails.skuba?.plan !== "enterprise"}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Configure seats and credits (for enterprise orgs)
                </DropdownMenuItem>
              </Link>
              <Link
                to="/organizations/view/$orgId"
                params={{ orgId }}
                search={{ sheet: "invite" }}
                className="text-sm text-primary hover:underline"
                disabled={org.orgDetails.skuba?.plan !== "enterprise"}
              >
                <DropdownMenuItem disabled={org.orgDetails.skuba?.plan !== "enterprise"}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Invite members (for enterprise orgs)
                </DropdownMenuItem>
              </Link>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-6">
        <OrgInfo
          key={org.workosOrg.id}
          org={{
            id: org.orgDetails.id,
            name: org.orgDetails.name,
            createdAt: org.workosOrg.createdAt,
            subscription: org.orgDetails.skuba,
            lastEnterpriseSubscription: org.orgDetails.lastEnterpriseSubscription,
            members: org.orgMembers,
            stripeCustomerId: org.orgDetails.stripeCustomerId,
            stripeKeyMode: org.orgDetails.stripeKeyMode,
            workos: { id: org.workosOrg.id },
            enterpriseSeatsCredits: org.orgDetails.enterpriseSeatsCredits,
          }}
          credits={org.orgBalance}
          billing={{
            appUserId: billingQuery.data?.revenueCat.appUserId ?? `org:${org.orgDetails.id}`,
            subscription: billingQuery.data?.revenueCat.subscription ?? null,
            isPending: billingQuery.isPending,
            isError: billingQuery.isError,
          }}
        />
      </div>
    </div>
  );
}
